import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete, MdKeyboardBackspace } from "react-icons/md";
import { FaHotel, FaRoute } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteTourBooking,
  getAllTourBookings,
} from "./services/tourBooking.services";
import {
  clearTourBookingFilterDetails,
  clearTourBookingID,
  setTourBookingID,
} from "./tourBookingSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import TourBookingFilter from "./TourBookingFilter";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";

const TourBooking: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const QuotationID = location.state?.QuotationID;

  const handleCreate = (QuotationID: number) => {
    navigate("/tourbooking/create", {
      state: {
        QuotationID: QuotationID,
      },
    });
  };

  return (
    <>
      <DeleteTourBookingDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton
              fillMode="solid"
              title="Back"
              onClick={() => navigate(-1)}
              icon={MdKeyboardBackspace}
              iconClassName="absolute-position"
              style={{ height: 30 }}
              iconStyle={{ fontSize: "20px" }}
            />
            <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
              Tour Booking List
            </Typography.h4>
          </div>
          {isAddAccess && QuotationID && (
            <Button
              onClick={() => handleCreate(QuotationID)}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <TourBookingGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const TourBookingGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const isEditBtnRef = useRef<any>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isHotelAddAccess = checkAcessRights("/hotelvoucher", INSERT_ACCESS);
  const isRouteAddAccess = checkAcessRights("/routevoucher", INSERT_ACCESS);

  const ContactID = location.state?.ContactID;
  const LeadID = location.state?.LeadID;
  const InquiryID = location.state?.InquiryID;
  const QuotationID = location.state?.QuotationID;

  const TourBookingList = useAppSelector(
    (state) => state.tourBooking.TourBookingList
  );
  const loading = useAppSelector((state) => state.tourBooking.loading);
  const TourBookingFilterDetails = useAppSelector(
    (state) => state.tourBooking.TourBookingFilterDetails
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  useEffect(() => {
    const matchFilter = (id: any, filterId: any) =>
      (!id && !filterId) || (id && filterId);

    const hasMatchingFilterID =
      matchFilter(ContactID, TourBookingFilterDetails?.ContactID) &&
      matchFilter(LeadID, TourBookingFilterDetails?.LeadID) &&
      matchFilter(InquiryID, TourBookingFilterDetails?.InquiryID) &&
      matchFilter(QuotationID, TourBookingFilterDetails?.QuotationID);

    if (hasMatchingFilterID) {
      dispatch(
        getAllTourBookings({
          ...TourBookingFilterDetails,
          Limit: +pageLimit,
          Skip: +currentPage * +pageLimit,
        })
      );
    }
  }, [TourBookingFilterDetails, currentPage, pageLimit]);

  useEffect(() => {
    return () => {
      if (!isEditBtnRef.current) {
        dispatch(setCurrentPage(0));
        dispatch(setPageLimit(50));
        dispatch(clearTourBookingFilterDetails());
      }
    };
  }, []);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (item: any) => {
      navigate("/tourbooking/edit", {
        state: {
          TourBookingID: item?.ID,
        },
      });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteTourBooking"));
      dispatch(setTourBookingID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.style, right: 0 }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => {
              isEditBtnRef.current = true;
              handleEdit(props.dataItem);
            }}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 11 || props.index === 12 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div
        style={{
          marginBottom: "10px",
          padding: "10px",
          border: "1px solid lightgray",
          borderRadius: "5px",
          boxShadow: "0 0 10px gainsboro",
        }}
      >
        <TourBookingFilter />
      </div>
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={false}
        resizable={true}
        reorderable={true}
        data={TourBookingList}
        total={TourBookingList[0]?.TotalRecord || TourBookingList?.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 50, 100, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="TourBookingNo"
          title="Booking No"
          width={150}
          cell={(props: any) => (
            <td>
              <p
                className="hoverableName"
                style={{ fontWeight: 600, cursor: "pointer", margin: 0 }}
                onClick={() =>
                  window.open(
                    `/tourbookingdetails/${props.dataItem?.TourBookingNo}`
                  )
                }
              >
                {props.dataItem?.TourBookingNo}
              </p>
            </td>
          )}
        />
        <Column field="Name" title="Tourist Name" width={200} />
        <Column field="MobileNo" title="Contact No" width={150} />
        <Column field="SectorName" title="Sector" width={150} />
        <Column field="TravelType" title="Travel Type" width={150} />
        <Column field="TourName" title="Tour" width={350} />
        <Column field="TourDateCode" title="Tour Code" width={150} />
        <Column field="NoOfNights" title="Nights" width={150} />
        <Column field="IsCancelled" title="Status" width={150} />
        <Column field="BookBy" title="Book By" width={200} />
        <Column field="BranchName" title="Branch" width={150} />
        {(isHotelAddAccess || isRouteAddAccess) && (
          <Column
            field="ID"
            title="Vouchers"
            width={"110px"}
            filterable={false}
            locked={true}
            cell={(props) => {
              return (
                <td
                  style={{
                    display: "flex",
                    gap: 5,
                    justifyContent: "center",
                    ...props.style,
                    right: 110,
                  }}
                  className={props.className}
                >
                  {isHotelAddAccess && (
                    <IconButton
                      type="button"
                      fillMode="flat"
                      size="small"
                      title="Hotel Voucher"
                      onClick={() =>
                        navigate("/hotelvoucher/edit", {
                          state: {
                            // TourBookingID: props.dataItem.ID,
                            TourBookingNo: props.dataItem.TourBookingNo,
                          },
                        })
                      }
                      icon={FaHotel}
                      iconClassName="absolute-position"
                      iconStyle={{ fontSize: "20px" }}
                    />
                  )}
                  {isRouteAddAccess && (
                    <IconButton
                      type="button"
                      fillMode="flat"
                      size="small"
                      title="Route Voucher"
                      onClick={() =>
                        navigate("/routevoucher/edit", {
                          state: {
                            TourBookingID: props.dataItem.ID,
                            TourBookingNo: props.dataItem.TourBookingNo,
                          },
                        })
                      }
                      icon={FaRoute}
                      iconClassName="absolute-position"
                      iconStyle={{ fontSize: "20px" }}
                    />
                  )}
                </td>
              );
            }}
          />
        )}
        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="ID"
            title="Actions"
            cell={MyEditCommandCell}
            width={"110px"}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeleteTourBookingDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const TourBookingID = useAppSelector(
    (state) => state.tourBooking.TourBookingID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const TourBookingFilterDetails = useAppSelector(
    (state) => state.tourBooking.TourBookingFilterDetails
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteTourBooking(ID));

      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getAllTourBookings(TourBookingFilterDetails));
        dispatch(closeDialog());
        dispatch(clearTourBookingID());
      } else {
        dispatch(getAllTourBookings(TourBookingFilterDetails));
        dispatch(closeDialog());
        dispatch(clearTourBookingID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteTourBooking" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Tour Booking"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Tour Booking?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(TourBookingID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default TourBooking;
